import React, { useState } from "react";
import numeral from "numeral";
import moment from "moment";

import useD3 from "../../../../Shared/use-d3";
import {
    scaleLinear,
    scaleUtc,
    extent,
    area,
    curveStep,
    axisBottom,
    axisLeft,
    max,
    timeFormat,
    timeMonth,
    timeYear,
    format,
    curveCatmullRom,
    line,
    curveNatural,
    bisectLeft,
    pointer,
    bisect,
    bisector
} from "d3";
import { css } from "@emotion/react";

const margin = { top: 20, right: 25, bottom: 30, left: 40 };

export default function EpisodeChart({ data, dateExtent, period }) {
    const [{ tipVisible, date, downloads }, setState] = useState({});

    const [elemRef, dimensionRef] = useD3(
        (svg, { width, height }) => {
            const w = width - margin.left - margin.right,
                h = height - margin.top - margin.bottom;

            const xScale = scaleUtc()
                .domain(extent(dateExtent, d => new Date(d)))
                .range([0, w]);

            const xAxis = axisBottom(xScale);

            // TODO: this really needs more sophisticated counting of
            // the number of periods being charted, so we don't get too
            // many ticks.
            //let interval;
            //switch (period) {
            //    case "month": {
            //        interval = timeMonth;
            //        xAxis.ticks(interval);
            //        xAxis.tickFormat(timeFormat("%b %Y"));
            //        break;
            //    }
            //    case "quarter": {
            //        interval = timeMonth.every(3);
            //        xAxis.ticks(interval);
            //        xAxis.tickFormat(timeFormat("Q%q %Y"));
            //        break;
            //    }
            //    case "year": {
            //        interval = timeYear.every(1);
            //        xAxis.ticks(interval);
            //        xAxis.tickFormat(timeFormat("%Y"));
            //        break;
            //    }
            //}

            const valueDomain = [0, max(data, d => d[1]) || 1000];

            const yScale = scaleLinear()
                .domain(valueDomain)
                .range([h, 0]);

            const totalArea = area()
                .curve(curveStep)
                .x(d => xScale(new Date(d[0])))
                .y1(d => yScale(d[1]))
                .y0(d => yScale(0))
                .defined(d => d[1] >= 0);

            const yAxis = axisLeft(yScale)
                .ticks(4)
                .tickFormat(format("~s"));

            svg.select(".chart-area").attr("transform", `translate(${margin.left},${margin.top})`);
            svg.select(".x-axis").attr("transform", `translate(${margin.left},${margin.top + h})`);
            svg.select(".y-axis").attr("transform", `translate(${margin.left},${margin.top})`);

            svg
                .select(".line")
                .datum(data)
                .attr("fill", "#C9E3EE")
                .attr("fill-opacity", 0.9)
                //.attr("stroke", "none");
                .attr("stroke", "#3689AB")
                .attr("stroke-opacity", 0.7)
                .attr("d", totalArea);
            //.attr("stroke-width", 0.5);

            svg.select(".x-axis").call(xAxis);
            svg.select(".y-axis").call(yAxis);

            const [x1, x2] = xScale.range();
            svg
                .select(".grid")
                .selectAll("line.grid-y")
                .data(yScale.ticks())
                .join("line")
                .attr("class", "grid-y")
                .attr("x1", x1)
                .attr("y1", yScale)
                .attr("x2", x2)
                .attr("y2", yScale)
                .attr("stroke", "#eee");

            const [y1, y2] = yScale.range();
            svg
                .select(".grid")
                .selectAll("line.grid-x")
                .data(xScale.ticks())
                .join("line")
                .attr("class", "grid-x")
                .attr("x1", xScale)
                .attr("y1", y1)
                .attr("x2", xScale)
                .attr("y2", y2)
                .attr("stroke", "#eee");

            const pointerX = svg
                .select(".pointer")
                .attr("stroke", "#666")
                .attr("stroke-dasharray", "4, 2")
                .attr("d", `M 0 0 V ${h}`)
                .style("display", "none");

            const bisectDate = bisector(d => new Date(d[0])).left;

            function mouseout() {
                pointerX.style("display", "none");
                setState(state => ({ ...state, tipVisible: false }));
            }

            function mouseover() {
                pointerX.style("display", null);
                setState(state => ({ ...state, tipVisible: true }));
            }

            function mousemove(event) {
                const x0 = xScale.invert(pointer(event)[0]),
                    i = bisectDate(data, x0, 1),
                    d0 = data[i - 1],
                    d1 = data[i];
                //console.log({ x0, i, d0, d1 });
                if (d0 && d1) {
                    const d = x0 - new Date(d0[0]) > new Date(d1[0]) - x0 ? d1 : d0;
                    setState(state => ({ ...state, date: d[0], downloads: d[1] }));
                    pointerX
                        .attr("transform", `translate(${xScale(new Date(d[0]))}, 0)`)
                        .style("display", null);
                } else {
                    mouseout();
                }
            }

            const mouseRect = svg
                .select(".mouse")
                .attr("fill", "none")
                .style("pointer-events", "all")
                .attr("width", w)
                .attr("height", h)
                .attr("transform", `translate(${margin.left},${margin.top})`)
                .on("mouseout", mouseout)
                .on("mouseover", mouseover)
                .on("mousemove", mousemove);
        },
        [data, dateExtent, period]
    );

    return (
        <div ref={dimensionRef} style={{ position: "relative" }}>
            <div
                style={{
                    pointerEvents: "none",
                    position: "absolute",
                    left: margin.left + 2,
                    top: margin.top,
                    padding: "1em",
                    background: "rgba(255,255,255,0.85)",
                    display: tipVisible ? "grid" : "none",
                    gridTemplateColumns: "auto auto",
                    gridGap: "0.5em"
                }}
            >
                <div style={{ color: "#000", whiteSpace: "nowrap" }}>
                    {date && moment(date).format("ll")}
                </div>
                <div
                    style={{
                        color: "#000",
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                        textAlign: "right"
                    }}
                >
                    {downloads && numeral(downloads).format("#,#")}
                </div>
            </div>
            <svg
                ref={elemRef}
                css={css`
                    border: 1px solid #eee;
                    border-bottom: none;
                    display: block;
                    height: 15vh;
                    width: 100%;
                    & .axis {
                        color: #777;
                        shape-rendering: crispEdges;
                    }
                `}
            >
                <g className="chart-area">
                    <g className="grid" />
                    <path className="line" />
                    <path className="pointer" />
                </g>
                <g className="axis y-axis" />
                <g className="axis x-axis" />
                <rect className="mouse" />
            </svg>
        </div>
    );
}
